import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { formatVND } from "@/lib/format";

type PayrollData = {
  phone: string;
  name: string;
  totalHours: number;
  totalPay: number;
  hourlyRate: number;
  payslipUrl: string | null;
};

export function EmployeePayslip() {
  const { weekId, phone } = useParams<{ weekId: string; phone: string }>();
  const [payroll, setPayroll] = useState<PayrollData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!weekId || !phone) return;
    return onSnapshot(doc(db, "weeks", weekId, "payroll", phone), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setPayroll({
          phone: data.phone as string,
          name: data.name as string,
          totalHours: data.totalHours as number,
          totalPay: data.totalPay as number,
          hourlyRate: data.hourlyRate as number,
          payslipUrl: (data.payslipUrl as string) ?? null,
        });
      }
      setLoading(false);
    });
  }, [weekId, phone]);

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Đang tải payslip…</div>;
  }

  if (!payroll) {
    return (
      <div className="p-8 text-center text-gray-500">
        Không tìm thấy payslip. Hãy yêu cầu quản lý xuất payslip trước.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-8">
      <div className="rounded-3xl bg-white p-8 shadow-xl ring-1 ring-pink-100">
        <div className="text-center">
          <div className="text-5xl">🧾</div>
          <h1 className="mt-3 font-display text-2xl font-black text-gray-900">
            {payroll.name}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Payslip tuần {weekId}
          </p>
        </div>

        <div className="mt-8 space-y-3">
          <div className="flex justify-between rounded-xl bg-pink-50 p-4">
            <span className="text-sm font-medium text-gray-600">Tổng giờ</span>
            <span className="font-bold text-gray-900">{payroll.totalHours} giờ</span>
          </div>
          <div className="flex justify-between rounded-xl bg-pink-50 p-4">
            <span className="text-sm font-medium text-gray-600">Lương / giờ</span>
            <span className="font-bold text-gray-900">{formatVND(payroll.hourlyRate)}</span>
          </div>
          <div className="flex justify-between rounded-xl bg-pink-100 p-4">
            <span className="text-sm font-bold text-pink-700">Tổng lương</span>
            <span className="font-black text-lg text-pink-600">
              {formatVND(payroll.totalPay)}
            </span>
          </div>
        </div>

        {payroll.payslipUrl && (
          <div className="mt-6">
            <a
              href={payroll.payslipUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-cta inline-flex w-full items-center justify-center gap-2"
            >
              ⬇ Tải payslip PNG
            </a>
          </div>
        )}

        {!payroll.payslipUrl && (
          <div className="mt-6 rounded-xl border border-dashed border-gray-300 p-4 text-center text-sm text-gray-400">
            Payslip đang được tạo. Vui lòng đợi quản lý xuất payslip.
          </div>
        )}
      </div>
    </div>
  );
}
